<template>
    <section
        ref="audioContainer"
        class="d-flex flex-column justify-content-center align-items-center p-32 w-100 mx-auto"
        style="max-width: 580px"
        tabindex="0"
        @keydown.space.prevent="playToggle"
        @keydown.left="timeBack"
        @keydown.right="timeForward"
    >
        <div
            class="d-flex justify-content-center align-items-center rounded bg--cover"
            :style="posterStyle"
        >
            <v-icon
                name="headphones"
                :w="180"
                class="rounded"
                style="color: rgba(255, 255, 255, 0.5)"
            />
        </div>

        <audio
            ref="audio"
            class="audio--player w-100"
            :src="url"
            :autoplay="autoplay"
            :loop="loop"
            @ended="ended"
            @timeupdate="updateTimeline"
            @playing.once="$emit('accessed')"
            @play="playing = true"
            @pause="playing = false"
            @volumechange="volumechange"
        />

        <div
            class="d-flex justify-content-center align-items-center w-100 px-12"
            style="height: 64px"
            @mouseleave="showVolumeBar = false"
        >
            <time-bar
                class="text-light"
                :current-time="currentTime"
                :duration="totalDuration"
                @input="newTime => updateProgress(newTime)"
            />

            <button-link
                :icon="isMuted ? 'volume-mute' : 'volume'"
                size="34"
                theme="light"
                :aria-label="
                    ucfirst(
                        isMuted
                            ? $t('common.activateSound')
                            : $t('common.silence')
                    )
                "
                class="ml-18"
                @click="mute"
                @mouseenter="showVolumeBar = true"
            />

            <volume-bar
                class="transition-width transition-duration-50 transition-ease ml-4"
                :class="showVolumeBar ? 'opacity--100' : 'opacity--0 w-0'"
                :value="volume"
                style="width: 72px"
                @update:value="newVolume => adjustVolume(newVolume)"
            />
        </div>

        <span
            v-if="controls"
            class="position-relative d-flex align-items-center mx-auto mt-20"
        >
            <button-link
                icon="history"
                size="34"
                theme="light"
                @click="timeBack"
            />

            <play-button
                ref="pause-play"
                :size="56"
                :aria-label="
                    ucfirst(playing ? $t('common.pause') : $t('common.play'))
                "
                :is-playing="playing"
                class="mx-32"
                @click="playToggle"
            />

            <button-link
                icon="history-left"
                size="34"
                theme="light"
                @click="timeForward"
            />
        </span>

        <tool-tip name="pause-play">
            {{ ucfirst(playing ? $t("common.pause") : $t("common.play")) }}
        </tool-tip>
    </section>
</template>

<script>
const JUMP_LENGTH = 5 // seconds
import { clamp, focusFirstFocusable, imageWithVeil, ucfirst } from "~/lib/utils"

import ButtonLink from "~/components/buttons/ButtonLink"
import PlayButton from "~/components/buttons/PlayButton"
import TimeBar from "~/components/TimeBar"
import ToolTip from "~/components/ToolTip"
import VolumeBar from "~/components/players/Video/VolumeBar"

export default {
    props: {
        url: {
            type: String,
            required: true,
        },

        autoplay: {
            type: Boolean,
            default: true,
        },

        controls: {
            type: Boolean,
            default: true,
        },

        loop: {
            type: Boolean,
            default: false,
        },

        posterUrl: {
            type: String,
            default: "",
        },

        focusOnMount: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["accessed"],

    data() {
        return {
            currentTime: 0,
            totalDuration: 0,
            progress: 0,
            playing: false,

            // This "complicated" management is due to Safari iOS not allowing changing
            // the volume via JS. This way, at least we don't lie and show the true volume.
            volume: 1,
            targetVolume: 1,
            previousTargetVolume: 1,

            isMuted: false,
            loadingError: false,
            showVolumeBar: false,
        }
    },

    computed: {
        posterStyle() {
            return {
                height: "20rem",
                width: "20rem",
                marginBottom: "80px",
                backgroundImage: imageWithVeil(this.posterUrl),
            }
        },
    },

    mounted() {
        if (this.focusOnMount) {
            focusFirstFocusable(this.$refs.audioContainer)
        }
    },

    methods: {
        playToggle() {
            this.$refs.audio.paused ? this.play() : this.pause()
        },

        async play(time) {
            if (!this.$refs.audio || isNaN(this.$refs.audio.duration)) {
                this.loadingError = true
                return
            }

            try {
                await this.$refs.audio.play()
                if (time) {
                    this.$refs.audio.currentTime = time
                }
            } catch (error) {
                this.$store.dispatch("pushNotification", {
                    title: error,
                    info: error.message,
                    type: "error",
                })
            }
        },

        pause() {
            if (!this.$refs.audio) return
            this.$refs.audio.pause()
        },

        ended() {
            if (this.loop) return
            this.pause()
        },

        updateTimeline() {
            if (!this.$refs.audio) return
            let totalDuration = this.$refs.audio.duration
            let currentTime = this.$refs.audio.currentTime
            this.progress = Math.floor((100 * currentTime) / totalDuration)
            this.currentTime = currentTime || this.currentTime
            this.totalDuration = totalDuration || this.totalDuration
        },

        timeBack() {
            let newTime = clamp(
                this.$refs.audio.currentTime - JUMP_LENGTH,
                0,
                this.$refs.audio.duration
            )
            this.updateProgress(newTime)
        },

        timeForward() {
            let newTime = clamp(
                this.$refs.audio.currentTime + JUMP_LENGTH,
                0,
                this.$refs.audio.duration
            )
            this.updateProgress(newTime)
        },

        updateProgress(newTime) {
            this.$refs.audio.currentTime = newTime
            this.currentTime = newTime
            this.progress = (newTime * 100) / this.$refs.audio.duration
        },

        mute() {
            if (this.targetVolume == 0) {
                this.targetVolume = this.previousTargetVolume
            } else {
                this.previousTargetVolume = this.targetVolume
                this.targetVolume = 0
            }
            this.applyVolume()
        },

        adjustVolume(newVolume) {
            this.previousTargetVolume = this.targetVolume
            this.targetVolume = newVolume
            this.applyVolume()
        },

        applyVolume() {
            if (!this.$refs.audio) return
            this.$refs.audio.volume = this.targetVolume
        },

        volumechange() {
            this.volume = this.$refs.audio.volume
            this.isMuted = this.targetVolume === 0
        },

        ucfirst,
    },

    components: {
        ButtonLink,
        PlayButton,
        TimeBar,
        ToolTip,
        VolumeBar,
    },
}
</script>
